import type { ReactNode } from 'react';

import { Loader } from '@/components/Loader';
import { cls } from '@/utils/cls';

type QueryState = 'error' | 'loading' | 'empty' | 'ready';

type Props = {
  emptyMessage: string;
  error: Error | null;
  errorMessage: string;
  hasData: boolean;
  isFetching?: boolean;
  isLoading: boolean;
  loadingMessage: string;
  table: ReactNode;
};

function deriveQueryState({ error, hasData, isLoading }: Pick<Props, 'error' | 'hasData' | 'isLoading'>): QueryState {
  if (error) return 'error';
  if (isLoading) return 'loading';
  if (!hasData) return 'empty';
  return 'ready';
}

function getStateMessage(
  state: Exclude<QueryState, 'ready'>,
  messages: Pick<Props, 'emptyMessage' | 'errorMessage' | 'loadingMessage'>,
) {
  return {
    error: messages.errorMessage,
    loading: messages.loadingMessage,
    empty: messages.emptyMessage,
  }[state];
}

export function TableQueryStateContent({
  emptyMessage,
  error,
  errorMessage,
  hasData,
  isFetching = false,
  isLoading,
  loadingMessage,
  table,
}: Props) {
  const state = deriveQueryState({ error, hasData, isLoading });

  if (state !== 'ready') {
    if (state === 'loading') {
      return <Loader containerClass="mt-3 min-h-[260px] border-0 bg-transparent p-0 shadow-none" />;
    }

    return (
      <p className={cls('py-6 text-center', state === 'error' ? 'text-red-600' : 'text-gray-400')}>
        {getStateMessage(state, { emptyMessage, errorMessage, loadingMessage })}
      </p>
    );
  }

  return (
    <div className="relative">
      <div className={cls('transition-opacity', isFetching && 'opacity-60')}>{table}</div>
      {isFetching ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <Loader containerClass="h-full w-full border-0 bg-transparent p-0 shadow-none" iconClass="size-10 border-4" />
        </div>
      ) : null}
    </div>
  );
}
